// Global Instructions Rule Applied!
//
// The Commitments section of the Investor form: what the investor committed
// to — one row per standalone commitment and one per consolidated commitment
// (view__investor_commitments; the pockets of a consolidated commitment are
// not listed). The fund name opens the commitment's own form (the Commitment
// form, or the Consolidated commitment form); "+ Commitment" opens the New
// commitment form for this investor.
// The Notice documents column shows what each commitment's capital call
// notices carry — the investor's documents, or the commitment's own when it
// has its own setting.

import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { Button, Space, Table, Tag } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'

import { FormSection } from '../../../core/components/FormSection.tsx'
import GridSwitchCell from '../../../core/components/GridSwitchCell.tsx'
import HelpTip from '../../../core/components/HelpTip.tsx'
import type { InvestorCommitmentRow } from '../../../state_manager/data_models/investor.ts'
import ExportRowsButton from '../../shared/table_grid_export/ExportRowsButton.tsx'

interface Props {
  commitments: InvestorCommitmentRow[]
  /** The investor's name, for the Export file's name. */
  investorName?: string | null
  loading?: boolean
  /** Opens a commitment's form (by prog__kind). Absent = the fund is plain text. */
  onOpenCommitment?: (row: InvestorCommitmentRow) => void
  /** "+ Commitment": the New commitment form for this investor. */
  onAddCommitment?: () => void
}

const DATE_FORMAT = 'MM/DD/YYYY'
const GP_READ_ONLY_REASON = "Changed on the commitment's form, or in the GP column of the fund's Investors tab"
const NOTICE_DOCUMENTS_COLUMN_HELP =
  "What the commitment's capital call notices carry in addition to the notice itself: this investor's documents, " +
  'unless the commitment has its own setting.'

const fmtDate = (value: string | null): string => (value ? dayjs(value).format(DATE_FORMAT) : '-')
// GridSwitchCell needs a save; a disabled switch never calls it
const neverSaves = async (): Promise<boolean> => false

export function InvestorCommitmentsSection({
  commitments,
  investorName,
  loading = false,
  onOpenCommitment,
  onAddCommitment
}: Props): React.ReactElement {
  const columns = useMemo<ColumnsType<InvestorCommitmentRow>>(
    () => [
      {
        title: 'Fund',
        key: 'fund',
        render: (_: unknown, row) => {
          const name = (row.prog__kind === 'consolidated' ? row.fund_groups__name : row.funds__name) ?? '-'
          return (
            <span className='inline-flex items-center gap-2'>
              {onOpenCommitment ? (
                <button
                  type='button'
                  className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left'
                  onClick={() => onOpenCommitment(row)}
                >
                  {name}
                </button>
              ) : (
                name
              )}
              {row.prog__kind === 'consolidated' ? <Tag color='purple'>Consolidated</Tag> : null}
            </span>
          )
        }
      },
      {
        title: 'Status',
        dataIndex: 'fund_commitment_statuses__label',
        key: 'status',
        render: (value: string | null) => value ?? '-'
      },
      {
        title: 'Commitment',
        dataIndex: 'prog__amount_fmt',
        key: 'amount',
        align: 'right',
        render: (value: string | null) => value ?? '-'
      },
      {
        title: 'Date Committed',
        dataIndex: 'prog__date_committed',
        key: 'date_committed',
        render: fmtDate
      },
      {
        title: 'Date Closed',
        dataIndex: 'prog__date_closed',
        key: 'date_closed',
        render: fmtDate
      },
      {
        title: 'GP',
        key: 'gp',
        align: 'center',
        width: 70,
        render: (_: unknown, row) => (
          <GridSwitchCell
            checked={row.prog__is_gp_commitment}
            ariaLabel='GP commitment'
            disabledReason={GP_READ_ONLY_REASON}
            onSave={neverSaves}
          />
        )
      },
      {
        title: (
          <span>
            Notice documents
            <HelpTip title={NOTICE_DOCUMENTS_COLUMN_HELP} />
          </span>
        ),
        key: 'notice_documents',
        render: (_: unknown, row) => (
          <span className='inline-flex items-center gap-2 flex-wrap'>
            {row.prog__notice_supplement_labels.length > 0 ? row.prog__notice_supplement_labels.join(', ') : 'None'}
            {row.prog__notice_supplements_overridden ? <Tag color='blue'>Own setting</Tag> : null}
          </span>
        )
      }
    ],
    [onOpenCommitment]
  )

  return (
    <FormSection
      title='Commitments'
      titleColor='blue'
      scrollable={false}
      headerActions={
        <Space>
          <ExportRowsButton
            name={`${investorName || 'Investor'} Commitments`}
            rows={commitments}
            columns={[
              { title: 'Fund', value: (r) => (r.prog__kind === 'consolidated' ? r.fund_groups__name : r.funds__name) },
              // the Fund cell's Consolidated tag: which name the Fund column holds
              { title: 'Kind', value: (r) => (r.prog__kind === 'consolidated' ? 'Consolidated' : 'Fund') },
              { title: 'Status', value: (r) => r.fund_commitment_statuses__label },
              { title: 'Commitment', value: (r) => r.prog__amount },
              { title: 'Date Committed', value: (r) => r.prog__date_committed, kind: 'date' },
              { title: 'Date Closed', value: (r) => r.prog__date_closed, kind: 'date' },
              { title: 'GP', value: (r) => (r.prog__is_gp_commitment ? 'Yes' : 'No') },
              {
                title: 'Notice documents',
                value: (r) =>
                  r.prog__notice_supplement_labels.length > 0 ? r.prog__notice_supplement_labels.join(', ') : 'None'
              },
              { title: 'Own setting', value: (r) => (r.prog__notice_supplements_overridden ? 'Yes' : null) }
            ]}
          />
          {onAddCommitment ? (
            <Button size='small' type='primary' icon={<PlusOutlined />} onClick={onAddCommitment}>
              Commitment
            </Button>
          ) : null}
        </Space>
      }
    >
      <Table<InvestorCommitmentRow>
        rowKey={(row) => `${row.prog__kind}-${row.prog__commitment_id}`}
        dataSource={commitments}
        columns={columns}
        loading={loading}
        pagination={false}
        size='small'
        scroll={{ x: 'max-content' }}
        locale={{ emptyText: <div className='text-center text-gray-500 text-sm py-2'>No commitments</div> }}
      />
    </FormSection>
  )
}

export default InvestorCommitmentsSection
